import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  createHeroProject,
  getHeroBranches,
  getHeroCreateMeta,
  getHeroMeasures,
  getHeroProjectSteps,
  getHeroProjectTypes,
} from "@/lib/projects";
import type {
  HeroBranchLookup,
  HeroMeasureLookup,
  HeroProjectStepLookup,
  HeroProjectTypeLookup,
  Order,
} from "@/lib/projects";
import {
  branchSource,
  measureSource,
  projectStepSource,
  projectTypeSource,
} from "@/pages/projekte/sources";

export interface HeroCreateDialogProps {
  open: boolean;
  order: Order | null;
  onClose: () => void;
  onCreated?: (orderId: number, heroProjectId: number) => void;
}

interface FormState {
  branch: AutocompleteOption<HeroBranchLookup> | null;
  measure: AutocompleteOption<HeroMeasureLookup> | null;
  projectType: AutocompleteOption<HeroProjectTypeLookup> | null;
  projectStep: AutocompleteOption<HeroProjectStepLookup> | null;
}

const EMPTY_FORM: FormState = {
  branch: null,
  measure: null,
  projectType: null,
  projectStep: null,
};

function toOption<T extends { id: number }>(
  item: T | null | undefined,
  label: (value: T) => string,
): AutocompleteOption<T> | null {
  if (!item) return null;
  return { key: String(item.id), label: label(item), value: item };
}

export function HeroCreateDialog({
  open,
  order,
  onClose,
  onCreated,
}: HeroCreateDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [errors, setErrors] = useState<Set<string>>(new Set());

  const stepLoad = useMemo(
    () =>
      projectStepSource(
        form.projectType ? Number(form.projectType.value.id) : null,
      ),
    [form.projectType],
  );

  useEffect(() => {
    if (!open || !order?.id) return;

    let cancelled = false;
    setForm(EMPTY_FORM);
    setErrors(new Set());
    setHint(null);
    setLoadingMeta(true);

    void (async () => {
      try {
        const meta = await getHeroCreateMeta(order.id);
        if (cancelled) return;

        const branchId = meta.branch_id ?? null;
        const measureId = meta.measure_id ?? null;
        const typeId = meta.project_type_id ?? null;
        const stepId = meta.project_step_id ?? null;

        const [branches, measures, types, steps] = await Promise.all([
          branchId ? getHeroBranches().catch(() => []) : Promise.resolve([]),
          measureId ? getHeroMeasures().catch(() => []) : Promise.resolve([]),
          typeId ? getHeroProjectTypes().catch(() => []) : Promise.resolve([]),
          typeId && stepId
            ? getHeroProjectSteps(Number(typeId)).catch(() => [])
            : Promise.resolve([]),
        ]);
        if (cancelled) return;

        const branch =
          branchId != null
            ? (branches.find((b) => Number(b.id) === Number(branchId)) ?? null)
            : null;
        const measure =
          measureId != null
            ? (measures.find((m) => Number(m.id) === Number(measureId)) ?? null)
            : null;
        const projectType =
          typeId != null
            ? (types.find((t) => Number(t.id) === Number(typeId)) ?? null)
            : null;
        const projectStep =
          stepId != null
            ? (steps.find((s) => Number(s.id) === Number(stepId)) ?? null)
            : null;

        setForm({
          branch: toOption(branch, (b) => b.name),
          measure: toOption(
            measure,
            (m) => m.name || m.short || `#${m.id}`,
          ),
          projectType: toOption(projectType, (t) => t.name),
          projectStep: toOption(projectStep, (s) => s.name),
        });

        if (!typeId) {
          setHint("Kein Projekttyp hinterlegt – bitte prüfen.");
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unbekannter Fehler";
        toast.error(`Hero-Anlage vorbereiten fehlgeschlagen: ${message}`);
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, order?.id]);

  async function handleSubmit() {
    if (!order?.id) return;

    const missing = new Set<string>();
    if (!form.branch) missing.add("Niederlassung");
    if (!form.measure) missing.add("Gewerk");
    if (!form.projectType) missing.add("Projekttyp");
    if (!form.projectStep) missing.add("Status-Schritt");
    setErrors(missing);
    if (missing.size > 0) {
      toast.error(
        "Bitte Niederlassung, Gewerk, Projekttyp und Status-Schritt wählen.",
      );
      return;
    }

    setSubmitting(true);
    try {
      const result = await createHeroProject(order.id, {
        project_step_id: Number(form.projectStep!.value.id),
        measure_id: Number(form.measure!.value.id),
        branch_id: Number(form.branch!.value.id),
        project_type_id: Number(form.projectType!.value.id),
      });
      toast.success("Projekt wurde in Hero angelegt.");
      onCreated?.(order.id, Number(result.hero_project_id));
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unbekannter Fehler";
      toast.error(`Hero-Anlage fehlgeschlagen: ${message}`);
    } finally {
      setSubmitting(false);
    }
  }

  const auftragsnummer = order?.auftragsnummer?.trim() || null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Projekt in Hero anlegen"
      description={
        auftragsnummer
          ? `Auftrag ${auftragsnummer} in Hero anlegen.`
          : "Fehlendes Hero-Projekt für diesen Auftrag anlegen."
      }
      size="sm"
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={submitting} />
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={submitting}
            disabled={loadingMeta || submitting}
            onClick={() => void handleSubmit()}
          >
            Anlegen
          </Button>
        </>
      }
    >
      {loadingMeta ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-label-secondary">
          <Spinner />
          <span>Vorbereitung …</span>
        </div>
      ) : (
        <div className="space-y-3">
          <LockedAutocomplete<HeroBranchLookup>
            label="Niederlassung"
            required
            placeholder="Niederlassung wählen …"
            value={form.branch}
            loadOptions={branchSource}
            onChange={(opt) => setForm((prev) => ({ ...prev, branch: opt }))}
            error={errors.has("Niederlassung") ? "Pflichtfeld" : null}
          />
          <LockedAutocomplete<HeroMeasureLookup>
            label="Gewerk"
            required
            placeholder="Gewerk suchen …"
            value={form.measure}
            loadOptions={measureSource}
            onChange={(opt) => setForm((prev) => ({ ...prev, measure: opt }))}
            error={errors.has("Gewerk") ? "Pflichtfeld" : null}
          />
          <LockedAutocomplete<HeroProjectTypeLookup>
            label="Projekttyp"
            required
            placeholder="Projekttyp suchen …"
            value={form.projectType}
            loadOptions={projectTypeSource}
            onChange={(opt) =>
              setForm((prev) => ({
                ...prev,
                projectType: opt,
                projectStep: opt ? prev.projectStep : null,
              }))
            }
            error={errors.has("Projekttyp") ? "Pflichtfeld" : null}
          />
          <LockedAutocomplete<HeroProjectStepLookup>
            label="Status-Schritt"
            required
            placeholder={
              form.projectType
                ? "Schritt wählen …"
                : "Bitte zuerst Projekttyp wählen"
            }
            value={form.projectStep}
            loadOptions={stepLoad}
            onChange={(opt) =>
              setForm((prev) => ({ ...prev, projectStep: opt }))
            }
            disabled={!form.projectType}
            disabledHint={
              !form.projectType ? "Wählt zuerst den Projekttyp." : null
            }
            error={errors.has("Status-Schritt") ? "Pflichtfeld" : null}
          />
          {hint ? (
            <p className="text-xs text-label-tertiary">{hint}</p>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
